import React, { useMemo, useState } from 'react';
import { useFaqStore } from 'src/hooks/settings';
import { FaqResponse } from 'src/api';
import { Divider, Popover, Segmented, Skeleton } from 'antd';
import { useEffect } from 'react';
import styled from '@emotion/styled';
import { ClearChatIcon, CloseIcon, DownloadIcon, HelperIcon } from 'src/icons';
import { getAppointmentDetailsForAnalytics } from 'src/utils';
import { analytics } from 'src/services/analytics';
import { useAuthStore } from 'src/hooks/auth';
import { useScreenSize } from '@insight-health-ai/roseate/utils';
import { Button } from '@insight-health-ai/roseate/components';
import {
  SettingOutlined,
  FontSizeOutlined,
  PlusCircleFilled,
  MinusCircleFilled,
} from '@ant-design/icons';
import { useChatStore, useChatValidationStore } from 'src/hooks/chat';
import { useConfigStore } from 'src/hooks/settings/useConfigStore';
import { Modal as AntdModal } from 'antd';
import { useTranslation } from 'react-i18next';
import { i18n } from 'src/services/i18n';
import { Language } from 'src/locales';
import { ChevronLeft, ChevronRight } from '@insight-health-ai/roseate/icons';

const zoomStep = 0.1;

const SidePanelContainer = styled('div', {
  shouldForwardProp: (prop) =>
    !['isVisible', 'isMobile'].includes(prop as string),
})<{ isVisible: boolean; isMobile: boolean }>`
  display: flex;
  flex-shrink: 0;
  z-index: 10;
  position: relative;
  height: 100%;
  transition: all 0.2s ease-in-out;
  ${({ isMobile, isVisible }) =>
    isMobile &&
    `
      position: absolute;
      right: 0px;
      transform: translateX(${isVisible ? '0%' : '100%'});
    `}
`;

const FaqPanel = styled('div', {
  shouldForwardProp: (prop) => !['isOpen', 'isMobile'].includes(prop as string),
})<{ isOpen: boolean; isMobile: boolean }>`
  height: 100%;
  overflow: auto;
  transition: all 0.2s ease-in-out;
  width: ${({ isMobile }) => (isMobile ? '100%' : '24rem')};
  ${({ isOpen }) =>
    isOpen
      ? `
   position: relative;
   transform: translateX(0%);
  `
      : `
   position: fixed;
   transform: translateX(100%);
  `}
`;

class FaqHTML extends React.Component<{ faq: FaqResponse }> {
  hostElement: HTMLDivElement | null = null;

  componentDidMount(): void {
    if (this.hostElement) {
      const shadowRoot = this.hostElement.attachShadow({ mode: 'open' });
      shadowRoot.innerHTML = this.props.faq?.body ?? '';
      const style = document.createElement('style');
      style.innerHTML = `
        h1 {
          font-weight: 600;
        }
        h2 {
          font-weight: 500;
        }
        .stackedit__html {
          padding-left: 1rem;
          padding-right: 1rem;
        }
      `;
      shadowRoot.appendChild(style);
    }
  }

  render() {
    return <div ref={(el) => (this.hostElement = el)} />;
  }
}

const SidePanel: React.FC = () => {
  const { mediaMax } = useScreenSize();
  const isMobile = mediaMax('sm');
  const [slideOut, setSlideOut] = useState(false);
  const { open, loading, faq, togglePanel, fetchFaq } = useFaqStore();
  const { config } = useConfigStore();
  const isFormMode = config.mode === 'form';
  const { zoom, setZoom, defaultZoom, downloadTranscript, chat, clearChat } =
    useChatStore();
  const { appointment } = useAuthStore();
  const { t } = useTranslation('misc', { keyPrefix: 'settings' });

  const isSuperAdmin = appointment?.roles?.includes('superadmin');

  const isVisible = useMemo(() => {
    return !isMobile || slideOut;
  }, [isMobile, slideOut]);

  useEffect(() => {
    if (!isFormMode) fetchFaq();
  }, [isFormMode]);

  useEffect(() => {
    const appointmentDetails = getAppointmentDetailsForAnalytics(appointment!);
    if (open) {
      analytics.track('Opened FAQ Sidebar', appointmentDetails);
    } else {
      analytics.track('Closed FAQ Sidebar', appointmentDetails);
    }
  }, [open]);

  const renderFaqPanel = () => {
    if (loading) {
      return (
        <div className="flex flex-col gap-4 h-full w-full p-4">
          <Skeleton active paragraph={{ rows: 5 }} />
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      );
    }
    return (
      <div className="">
        <div className="sticky top-0 text-xl p-4 border-b bg-gray-100 border-gray-200 flex justify-between items-center z-10">
          {faq?.title}
          <CloseIcon className="cursor-pointer" onClick={togglePanel} />
        </div>
        {faq && <FaqHTML faq={faq} />}
      </div>
    );
  };

  const renderFaq = () => {
    return <HelperIcon onClick={togglePanel} fill="#0f9f67" />;
  };

  const renderControls = () => {
    const jsx = (
      <SettingOutlined style={{ fontSize: '1.625rem', color: '#0f9f67' }} />
    );

    const popoverContent = (
      <div className="flex flex-col gap-4">
        <div className="flex gap-4 text-md">
          <div className="flex items-center gap-2">
            <FontSizeOutlined /> {t('fontSize.label')}
          </div>
          <div className="flex items-center gap-2 text-primary text-2xl">
            <PlusCircleFilled
              disabled={zoom >= 1.5}
              onClick={() => {
                if (useChatStore.getState().zoom >= 1.5) {
                  return;
                }
                const newZoom = Math.min(
                  1.5,
                  useChatStore.getState().zoom + zoomStep
                );
                setZoom(newZoom);
              }}
            />
            <MinusCircleFilled
              disabled={zoom <= 0.5}
              onClick={() => {
                if (useChatStore.getState().zoom <= 0.5) {
                  return;
                }
                const newZoom = Math.max(
                  0.5,
                  useChatStore.getState().zoom - zoomStep
                );
                setZoom(newZoom);
              }}
            />
            {zoom !== defaultZoom && (
              <Button
                size="sm"
                shape="round"
                variant="outlined"
                onClick={() => setZoom(defaultZoom)}
              >
                {t('fontSize.reset')}
              </Button>
            )}
          </div>
        </div>
        {isSuperAdmin && (
          <>
            <Divider className="my-0" />
            <div
              className="flex p-2 gap-2 text-md items-center cursor-pointer hover:bg-gray-100 rounded-md"
              onClick={async () => {
                analytics.track(
                  'Transcript Download Lumi Chat',
                  getAppointmentDetailsForAnalytics(appointment!)
                );
                await downloadTranscript({
                  messages: chat.messages,
                  details: appointment!,
                });
              }}
            >
              <DownloadIcon size="sm" />
              {t('downloadTranscript.label')}
            </div>
            <div
              className="flex p-2 gap-2 text-md items-center cursor-pointer hover:bg-gray-100 rounded-md"
              onClick={() => {
                analytics.track(
                  'Reset Chat Lumi Chat',
                  getAppointmentDetailsForAnalytics(appointment!)
                );
                const { destroy } = AntdModal.confirm({
                  title: t('resetChat.confirm.title'),
                  content: t('resetChat.confirm.description'),
                  type: 'warning',
                  footer: (
                    <div className="flex justify-end items-center gap-2 pt-2">
                      <Button appearance="secondary" onClick={() => destroy()}>
                        {t('resetChat.confirm.cancel')}
                      </Button>
                      <Button
                        appearance="danger"
                        onClick={async () => {
                          await clearChat({
                            chatToken: useChatValidationStore.getState().token!,
                            uuid: appointment?.chat_uuid!,
                          });
                          destroy();
                        }}
                      >
                        {t('resetChat.confirm.okay')}
                      </Button>
                    </div>
                  ),
                });
              }}
            >
              <ClearChatIcon size="sm" />
              {t('resetChat.label')}
            </div>
            <Divider className="my-0" />
          </>
        )}
        <div className="flex items-center py-2 gap-2 text-md cursor-pointer rounded-md">
          {t('language.label')}
          <Segmented
            value={i18n.language}
            onChange={(value) => {
              i18n.changeLanguage(value as Language, {
                syncBackend: true,
                persist: true,
              });
            }}
            options={[
              { label: t('language.en-US'), value: 'en-US' },
              { label: t('language.es-ES'), value: 'es-ES' },
            ]}
          />
        </div>
      </div>
    );

    return (
      <Popover
        placement="leftTop"
        rootClassName="min-w-72"
        showArrow={false}
        content={popoverContent}
        trigger="click"
      >
        {jsx}
      </Popover>
    );
  };

  const jsx = (
    <>
      {!isFormMode && renderFaq()}
      {renderControls()}
    </>
  );

  const faqPanelJsx = !isFormMode ? (
    <FaqPanel
      id="faq-panel"
      className={`bg-gray-100 shadow-lg border-l`}
      isMobile={isMobile}
      isOpen={open}
    >
      {renderFaqPanel()}
    </FaqPanel>
  ) : null;

  return (
    <SidePanelContainer
      isVisible={isVisible}
      isMobile={isMobile}
      className="bg-white shadow-lg border-l"
    >
      {faqPanelJsx}
      <div className="h-full w-14 flex-shrink-0 items-center border-gray-100 flex flex-col gap-4 py-4 z-10 bg-white">
        {jsx}
      </div>
      {isMobile && (
        <span
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full text-primary text-2xl cursor-pointer py-4 bg-white shadow-xl rounded-l-md border border-gray-100 text-xl"
          onClick={() => setSlideOut(!slideOut)}
        >
          {slideOut ? <ChevronRight /> : <ChevronLeft />}
        </span>
      )}
    </SidePanelContainer>
  );
};

export { SidePanel };
